// Piezas comunes del tema dopamina. Solo lectura.
import Image from "next/image";
import { Star } from "lucide-react";
import type { ReactNode } from "react";
import type { MediaSlot, PageSection } from "../types";
import { EYEBROW, H2 } from "./ui";

/** «Imagen generada con IA» (6, marcas obligatorias): en las fotos de IA con gente. */
export function AiLabel({ className = "" }: { className?: string }) {
  return (
    // Sin rótulo visible sobre la foto (Rodri, 02-11-2026): el aviso queda para lectores de pantalla.
    <span className={"sr-only " + className}>
      Imagen generada con IA
    </span>
  );
}

/**
 * Un hueco sin ninguna foto (solo si Magnific no pudo hacer ninguna): un bloque con las manchas de la
 * marca, sin texto ni icono. El comprador nunca ve «Falta la foto» (Rodri, 02-11-2026); qué foto
 * falta queda en data-missing para quien revisa la tienda.
 */
export function Marker({ missing }: { missing: string; compact?: boolean }) {
  return <div aria-hidden="true" data-missing={missing} className="wl-soft absolute inset-0" />;
}

/**
 * Una foto en su caja: llena el hueco (object-cover, sin franjas); el recorte va entero sobre las
 * manchas de la marca. El padre pone el tamaño (aspect-* y relative).
 */
export function SlotPhoto({ slot, sizes, priority = false, compact = false, labelPosition = "bottom-3 left-3" }: { slot: MediaSlot; sizes: string; priority?: boolean; compact?: boolean; labelPosition?: string }) {
  const photo = slot.photo;
  if (photo === undefined) return <Marker missing={slot.missing} compact={compact} />;
  const cutout = photo.kind === "recorte";
  return (
    <>
      {cutout ? <div aria-hidden="true" className="wl-soft absolute inset-0" /> : null}
      <Image
        src={photo.src}
        alt={photo.alt}
        fill
        sizes={sizes}
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : "auto"}
        className={cutout ? "object-contain p-[9%] drop-shadow-[0_24px_30px_rgb(0_0_0/0.18)]" : "object-cover"}
      />
      {photo.ai === true && photo.people !== false && !compact ? <AiLabel className={labelPosition} /> : null}
    </>
  );
}

export function Stars({ rating, size = "size-4" }: { rating: number; size?: string }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={rating.toFixed(1).replace(".", ",") + " de 5 estrellas"}>
      {[1, 2, 3, 4, 5].map((index) => (
        <Star key={index} aria-hidden="true" className={size + " " + (index <= Math.round(rating) ? "fill-(--wl-star) text-(--wl-star)" : "text-(--wl-line)")} strokeWidth={1.5} />
      ))}
    </span>
  );
}

/** El titular con sus 1-2 palabras clave en --grad-text (7.1). */
export function Accent({ text, accent }: { text: string; accent: string }) {
  const index = accent ? text.lastIndexOf(accent) : -1;
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <span className="wl-grad-text">{accent}</span>
      {text.slice(index + accent.length)}
    </>
  );
}

/** Sobretítulo y titular de una sección; en el bloque oscuro y en el degradado, el acento que se lee ahí. */
export function SectionHead({ section, title, intro, center = false, children }: { section: PageSection<string>; title: ReactNode; intro?: string; center?: boolean; children?: ReactNode }) {
  const onColor = section.tone === "dark" || section.tone === "accent";
  return (
    <div className={center ? "mx-auto max-w-[44rem] text-center" : "max-w-[44rem]"}>
      {section.eyebrow ? (
        <p className={EYEBROW + (section.tone === "dark" ? " text-(--wl-accent-on-dark)" : section.tone === "accent" ? " opacity-90" : " text-(--wl-accent-text)")}>{section.eyebrow}</p>
      ) : null}
      <h2 id={section.id + "-t"} className={H2 + (section.eyebrow ? " mt-2 lg:mt-3" : "")}>{title}</h2>
      {intro ? <p className={"mt-4 text-base leading-relaxed text-pretty lg:text-lg" + (onColor ? " opacity-90" : " text-(--wl-ink-muted)")}>{intro}</p> : null}
      {children}
    </div>
  );
}
